import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, CircularProgress, Container, Divider, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AmexLogo,
  DinersLogo,
  DiscoverLogo,
  GenericCardLogo,
  MasterCardLogo,
  VisaCardLogo,
} from '../../../../../shared/lib/assets';
import { Alert } from '../../../../../shared/lib/common/Alert';
import { PageHeading } from '../../../../../shared/lib/common/PageHeading';
import {
  CardBrands,
  DATE_FORMAT,
} from '../../../../../shared/lib/constants';
import palette from '../../../../../shared/lib/theme/palette';
import { BillingInvoice } from '../../../components/billingInvoice';
import { DashboardContainer } from '../../../components/dashboard/DashboardContainer';
import { EditAccountName } from '../../../components/accountSettings/EditAccountName';
import { EditUserEmail } from '../../../components/accountSettings/EditUserEmail';
import { Plan, useFetchCardDetailsLazyQuery, useGetSubscriptionDetailLazyQuery } from '../../../generated';
import { useAppContext } from '../../contexts/AppContext';
import { useFromMobile } from '../../hooks/useFromMobile';
import { LINKS } from '../../constants';

const MobileAccount = () => {
  const fromMobile = useFromMobile();
  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId } = isSubscribed ?? {};

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(LINKS.PORTAL_LOGIN_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [currentPlan, setCurrentPlan] = useState<Plan>();
  const [memberSince, setMemberSince] = useState<string>();
  const [nextBillingDate, setNextBillingDate] = useState<string>();
  const [cardBrand, setCardBrand] = useState<string>('');
  const [cardLast4, setCardLast4] = useState<string>('');

  const [fetchSubscription, { loading: subscriptionLoading }] = useGetSubscriptionDetailLazyQuery({
    fetchPolicy: 'network-only',
    onError({ message }) {
      Alert.error(message);
    },
    onCompleted(data) {
      const { getSubscriptionDetail } = data || {};
      if (getSubscriptionDetail) {
        const { data: detailData } = getSubscriptionDetail || {};
        const { items, current_period_end } = detailData[0] || {};
        const { data: itemsData } = items || {};
        const { plan, created } = itemsData.slice(-1)[0] || {};
        setCurrentPlan(plan);
        setMemberSince(created?.toString());
        setNextBillingDate(current_period_end?.toString());
      }
    },
  });

  const [fetchCardDetails, { loading: cardLoading }] = useFetchCardDetailsLazyQuery({
    fetchPolicy: 'network-only',
    onError({ message }) {
      Alert.error(message);
    },
    onCompleted(data) {
      const { fetchCardDetails } = data;
      if (fetchCardDetails) {
        const { brand, last4 } = fetchCardDetails;
        setCardBrand(brand ?? '');
        setCardLast4(last4 ?? '');
      }
    },
  });

  useEffect(() => {
    if (customerStripeId) {
      fetchSubscription({ variables: { customerStripeId } });
      fetchCardDetails({ variables: { fetchCardDetailsInput: customerStripeId } });
    }
  }, [customerStripeId]);

  const brandLogoMap: Record<string, () => JSX.Element> = {
    [CardBrands.VISA]: VisaCardLogo,
    [CardBrands.MASTERCARD]: MasterCardLogo,
    [CardBrands.American_Express]: AmexLogo,
    [CardBrands.DISCOVER]: DiscoverLogo,
    [CardBrands.DINERSCLUB]: DinersLogo,
    [CardBrands.GENERIC]: GenericCardLogo,
  };

  const renderCardLogo = (brand: string): JSX.Element => {
    const logoFn = brandLogoMap[brand.toLowerCase()] || GenericCardLogo;
    return logoFn();
  };

  const memberSinceFormatted =
    memberSince
      ? moment.tz(parseInt(memberSince) * 1000, 'UTC').format(DATE_FORMAT)
      : '';
  const nextBillingFormatted =
    nextBillingDate
      ? moment.tz(parseInt(nextBillingDate) * 1000, 'UTC').format(DATE_FORMAT)
      : '';

  const loading = subscriptionLoading || cardLoading;

  if (!fromMobile) {
    return (
      <DashboardContainer>
        <PageHeading color="blackOne" title="Account" subTitle="" />
        <BillingInvoice />
      </DashboardContainer>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="sm" sx={{ pt: 3, px: 2 }}>
        <Typography variant="h4" fontWeight={700} color={palette.blackOne} mb={2}>
          Account
        </Typography>

        {/* Options card */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${palette.lightBorderColor}`,
            borderRadius: 2,
            px: 2,
            py: 1.75,
            mb: 3,
            backgroundColor: '#fff',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" fontWeight={600} color={palette.blackOne}>
              If you want to:
            </Typography>
            <Button
              variant="outlined"
              size="small"
              component="a"
              href={LINKS.MOBILE_APP_DEEP_LINK}
              sx={{ textTransform: 'none', borderRadius: 2, fontSize: 12 }}
            >
              Open App
            </Button>
          </Box>
          {['Change your plan', 'Manage your payment method'].map((item) => (
            <Box key={item} display="flex" alignItems="center" gap={1} mb={0.5}>
              <FiberManualRecordIcon sx={{ fontSize: 7, color: palette.blackTwo }} />
              <Typography variant="body2" color={palette.blackTwo}>{item}</Typography>
            </Box>
          ))}
          <Box mt={1.25} display="flex" alignItems="center" flexWrap="wrap">
            <Typography variant="body2" color={palette.blackOne} component="span">
              Visit our web portal
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy URL'} placement="top">
              <IconButton size="small" onClick={handleCopy} sx={{ p: 0, mx: 0.5, color: '#1890FF', verticalAlign: 'middle' }}>
                {copied ? <CheckIcon sx={{ fontSize: 22 }} /> : <ContentCopyIcon sx={{ fontSize: 22 }} />}
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color={palette.blackOne} component="span">
              ({LINKS.PORTAL_LOGIN_URL}) for more settings
            </Typography>
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color={palette.blackTwo} mb={1}
              sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '12px' }}
            >
              Membership Details
            </Typography>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${palette.lightBorderColor}`,
                backgroundColor: '#fff',
                mb: 3,
                overflow: 'hidden',
              }}
            >
              {/* Plan header strip */}
              <Box
                sx={{
                  backgroundColor: palette.primary.main,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box display="flex" flexDirection="column">
                  {currentPlan?.productDetails?.name && (
                    <Typography variant="caption" color="rgba(255,255,255,0.75)" lineHeight={1.2}>
                      {currentPlan.productDetails.name}
                    </Typography>
                  )}
                  <Typography variant="body1" fontWeight={700} color="#fff">
                    {currentPlan?.nickname ?? 'Subscription Plan'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                {/* Plan details rows */}
                {memberSinceFormatted && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color={palette.blackTwo}>Member since</Typography>
                    <Typography variant="body2" fontWeight={600} color={palette.blackOne}>{memberSinceFormatted}</Typography>
                  </Box>
                )}

                {nextBillingFormatted && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color={palette.blackTwo}>Next billing</Typography>
                    <Typography variant="body2" fontWeight={600} color={palette.blackOne}>{nextBillingFormatted}</Typography>
                  </Box>
                )}

                {cardLast4 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="body2" color={palette.blackTwo}>Payment method</Typography>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      {renderCardLogo(cardBrand)}
                      <Typography variant="body2" fontWeight={600} color={palette.blackOne}>
                        •••• {cardLast4}
                      </Typography>
                    </Box>
                  </Box>
                )}

              </Box>

              <Divider />

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 2, py: 1.5, cursor: 'pointer' }}
                onClick={() => navigate(LINKS.MOBILE_PAYMENT_HISTORY)}
              >
                <Typography variant="body2" fontWeight={600} color={palette.blackOne}>
                  Payment History
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 20, color: palette.blackTwo }} />
              </Box>
            </Paper>

            <Typography
              variant="body2"
              color={palette.blackTwo}
              mb={1}
              sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '12px' }}
            >
              Profile
            </Typography>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${palette.lightBorderColor}`,
                backgroundColor: '#fff',
                px: 2,
              }}
            >
              <EditAccountName />
              <EditUserEmail />
            </Paper>
          </>
        )}
      </Container>
    </Box>
  );
};

export default MobileAccount;
